// @givmo/app-charity — the console's squares, derived once (shell contract v3).
//
// Turns the ONE console table (`module-table.ts`) into the `ConsoleSquare[]` the popup, the landing
// rule and the preferences page all read, through the package's ONE derivation
// (`deriveConsoleSquares`): which squares render, what state each is in, where each goes (the ONE
// resolver against the live origin), the Admin Console tile LAST and badged for a capability holder,
// the caller's own request decoration, and which square is their chosen default. ONE hook feeds every
// consumer, because the popup offering a different set from the landing rule is two answers to one
// question.
//
// ⚠ THE STANDING COMES FROM THE CONSOLE CONTEXT, NOT FROM A QUERY OF THIS HOOK'S OWN — the context is
// the single reader of `/org/{ref}/me` (see its header), and a second observer here would subscribe
// at the exact moment it settles.
//
// ⚠ `current` IS DERIVED FROM THE ADDRESS BAR (OM-12): the shared `currentModuleFor` (segment
// boundary, longest match) over the mounted modules PLUS the Admin Console row, so the popup marks
// the management tile current on its own pages.

import { useCallback, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { currentModuleFor, deriveConsoleSquares, type ConsoleSquare } from '@givmo/org-console';
import { useCharityConsole } from './charity-console-context';
import {
  charityConsoleTable,
  charityModuleRoutes,
  liveOrigin,
  type CharityTableRow,
} from './module-table';

/** The squares this build derives for this person, in table order, the Admin Console last. */
export function useConsoleSquares(): ConsoleSquare<CharityTableRow>[] {
  const { standings, read } = useCharityConsole();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  return useMemo(() => {
    const origin = liveOrigin();
    const table = charityConsoleTable(origin);
    const currentSlug = currentModuleFor(pathname, [
      ...charityModuleRoutes(origin),
      ...table.filter((row) => row.adminConsole === true),
    ]);
    return deriveConsoleSquares(table, {
      standings,
      capabilities: read.capabilities,
      origin,
      currentSlug: currentSlug ?? null,
      pendingAccessRequests: read.pendingAccessRequests,
      // OM-33: the modules announced to the charity class and not offered to this charity.
      comingSoon: read.comingSoonModules,
    });
  }, [standings, read, pathname]);
}

/**
 * The host's in-app navigate. `frame` is the sidebar FRAME the navigation carries (Org Machinery
 * OM-18; `console/sidebar-frame.ts`): the sidebar's own rows pass theirs; every other door takes the
 * default — an explicit NONE, a module switch. Assignable to the design system's `ModuleNavigate`.
 */
export type InAppNavigate = (href: string, frame?: string | null) => void;

/**
 * Follow an in-app destination with the router instead of a document load.
 *
 * ⚠ IT IS ONLY EVER HANDED DESTINATIONS THE HOST MARKED `inApp`; the design system fires this for
 * nothing else, and every square keeps its `href` either way. A `#fragment` (the preferences page's
 * anchors) and a `?query` (the roster's per-module door) are carried into the router's own fields.
 *
 * ⚠ ONE callback serves the popup's tiles AND the sidebar's rows, so the frame cannot ride it
 * blindly (OM-18 design 4(b)): the sidebar passes `sidebar.frame`; the popup, the wordmark's
 * landing, the Apps squares and the roster's door carry the default `null`, so a module switch
 * opens the destination in its OWN frame whatever the last-module hint names.
 */
export function useModuleNavigate(): InAppNavigate {
  const navigate = useNavigate();
  return useCallback<InAppNavigate>(
    (href, frame = null) => {
      const url = new URL(href, 'http://in-app.invalid');
      const search = Object.fromEntries(url.searchParams.entries());
      void navigate({
        to: url.pathname,
        ...(Object.keys(search).length > 0 ? { search } : {}),
        ...(url.hash.length > 1 ? { hash: url.hash.slice(1) } : {}),
        state: { sidebarFrame: frame },
      });
    },
    [navigate],
  );
}
